import React, { useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { ServiceFormValues } from "../service-management-type";
import { createServiceCategory } from "../../../../../api/admin/service-management";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
  Box,
} from "@mui/material";

type NeedType = "at_need" | "pre_need";

interface Props {
  onClose: () => void;
  isReadOnly?: boolean;

  /**
   * OPTIONAL: keep your existing callback if parent wants it
   */
  handleAddCategory?: (name: string) => void;
  onCreated?: () => void;
}

const AddCategoryModal: React.FC<Props> = ({
  onClose,
  isReadOnly,
  handleAddCategory,
  onCreated
}) => {
  const { values, errors, setFieldError } =
    useFormikContext<ServiceFormValues>() as any;

  // local state (minimal changes from your existing code)
  const [categoryName, setCategoryName] = useState("");
  const [needTypes, setNeedTypes] = useState<NeedType[]>([]);
  const [categoryImage, setCategoryImage] = useState<File | null>(null);
  const [categoryDescription, setCategoryDescription] = useState("");

  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  //  Uniqueness: tries a few common locations inside formik values
  const existingCategoryNames = useMemo(() => {
    const list: any[] =
      (values as any)?.categories ??
      (values as any)?.serviceCategories ??
      (values as any)?.categoryOptions ??
      [];

    if (!Array.isArray(list)) return [];

    return list
      .map((c) => (typeof c === "string" ? c : c?.name))
      .filter(Boolean);
  }, [values]);

  const normalizedExisting = useMemo(() => {
    return new Set(
      existingCategoryNames.map((n: string) => n.trim().toLowerCase())
    );
  }, [existingCategoryNames]);

  const clearModalErrors = () => {
    setFieldError("addCategoryName", "");
    setFieldError("addCategoryNeedTypes", "");
    setFieldError("addCategoryImage", "");
    setFieldError("addCategoryApi", "");
    setApiError("");
  };

  const handleClose = () => {
    if (loading) return;
    clearModalErrors();
    onClose();
  };

  const validateUsingFormikContext = () => {
    let ok = true;
    const name = categoryName.trim();

    // 1) Category Name: required, min length, unique
    if (!name) {
      setFieldError("addCategoryName", "Category name is required");
      ok = false;
    } else if (name.length < 3) {
      setFieldError(
        "addCategoryName",
        "Category name should be at least 3 characters"
      );
      ok = false;
    } else if (normalizedExisting.has(name.toLowerCase())) {
      setFieldError(
        "addCategoryName",
        "Category name already exists (must be unique)"
      );
      ok = false;
    }

    // 2) Need Type required
    if (!needTypes.length) {
      setFieldError(
        "addCategoryNeedTypes",
        "Please select at least one need type"
      );
      ok = false;
    }

    // 3) Image required + supported formats
    if (!categoryImage) {
      setFieldError("addCategoryImage", "Category image is required");
      ok = false;
    } else {
      const allowed = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowed.includes(categoryImage.type)) {
        setFieldError("addCategoryImage", "Only JPG or PNG images are supported");
        ok = false;
      }
    }

    return ok;
  };

  const toggleNeedType = (type: NeedType) => {
    if (isReadOnly || loading) return;

    setNeedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
    setFieldError("addCategoryNeedTypes", "");
  };

  const handlePickFile = () => {
    if (isReadOnly || loading) return;
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    if (isReadOnly || loading) return;

    clearModalErrors();

    const isValid = validateUsingFormikContext();
    if (!isValid) return;

    try {
      setLoading(true);

      //  Build FormData exactly like your curl
      const formData = new FormData();
      formData.append("name", categoryName.trim());

      if (categoryImage) {
        formData.append("serviceCategoryImage", categoryImage);
      }

      formData.append("needTypes", JSON.stringify(needTypes));

      // Optional (only if backend supports)
      if (categoryDescription.trim()) {
        formData.append("description", categoryDescription.trim());
      }

      await createServiceCategory(formData);

      // keep existing callback
      handleAddCategory?.(categoryName.trim());
      onCreated?.();

      onClose();
    } catch (e: any) {
      const rawMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to create category";

      // Format messages like "SERVICE_CATEGORY_ALREADY_EXISTS" to "Service category already exists"
      const formatErrorMessage = (msg: string) => {
        if (!msg) return msg;
        // Check if message looks like an uppercase snake_case code
        if (msg === msg.toUpperCase() && msg.includes('_')) {
          const lowerCase = msg.replace(/_/g, ' ').toLowerCase();
          return lowerCase.charAt(0).toUpperCase() + lowerCase.slice(1);
        }
        return msg;
      };

      const msg = formatErrorMessage(rawMsg);
      setApiError(msg);
      setFieldError("addCategoryApi", msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Add Category
        </Typography>

        {/* 1) Category Name */}
        <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.75 }}>
          Category Name <span className="required">*</span>
        </Typography>

        <TextField
          fullWidth
          required
          size="small"
          placeholder="Enter category name"
          value={categoryName}
          onChange={(e) => {
            if (isReadOnly || loading) return;
            setCategoryName(e.target.value);
            setFieldError("addCategoryName", "");
          }}
          onBlur={() => {
            const name = categoryName.trim();
            if (!name) {
              setFieldError("addCategoryName", "Category name is required");
            } else if (name.length < 3) {
              setFieldError("addCategoryName", "Category name should be at least 3 characters");
            } else if (normalizedExisting.has(name.toLowerCase())) {
              setFieldError("addCategoryName", "Category name already exists (must be unique)");
            } else {
              setFieldError("addCategoryName", "");
            }
          }}
          error={!!errors?.addCategoryName}
          helperText={errors?.addCategoryName || undefined}
          slotProps={{
            htmlInput: {
              minLength: 3,
              maxLength: 200,
            },
            formHelperText: {
              sx: { marginLeft: 0, marginTop: "4px" },
            },
          }}
          sx={{
            //  override your global SCSS: input { height: 40px; padding: 0 12px; margin-bottom: 16px; }
            "& .MuiInputBase-input": {
              height: "auto !important",         //  stop forced 40px on the actual <input>
              padding: "6px 12px !important",    //  centered for size="small"
              marginBottom: "0 !important",      //  remove global margin-bottom
              boxSizing: "border-box",
            },

            //  keep the OUTER field height consistent
            "& .MuiOutlinedInput-root": {
              height: "40px",
              display: "flex",
              alignItems: "center",
            },

            //  optional: if your global CSS also affects focus/outline spacing
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px",
            },
          }}
        />




        {/* 2) Need Type */}
        <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.75, mt: 1 }}>
          Need Type <span className="required">*</span>
        </Typography>

        <FormGroup row sx={{ gap: 4, mb: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={needTypes.includes("at_need")}
                onChange={() => toggleNeedType("at_need")}
                disabled={!!isReadOnly || loading}
              />
            }
            label="At-need"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={needTypes.includes("pre_need")}
                onChange={() => toggleNeedType("pre_need")}
                disabled={!!isReadOnly || loading}
              />
            }
            label="Preneed"
          />
        </FormGroup>

        <Typography
          sx={{
            fontSize: 12,
            color: errors?.addCategoryNeedTypes ? "#d32f2f" : "transparent",
            mb: 1,
          }}
        >
          {errors?.addCategoryNeedTypes || " "}
        </Typography>

        {/* 3) Category Image */}
        <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.75 }}>
          Category Image <span className="required">*</span>
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
          onChange={(e) => {
            if (isReadOnly || loading) return;

            const file = e.target.files?.[0];
            if (!file) return;

            //  Allowed file types (same style as your other file)
            const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
            if (!allowedTypes.includes(file.type)) {
              setFieldError(
                "addCategoryImage",
                "Invalid file type. Only .jpg, .jpeg, .png files are allowed."
              );
              setCategoryImage(null);
              // reset so user can pick same file again after error
              e.target.value = "";
              return;
            }

            //  Max file size 10MB
            const maxSizeMB = 10;
            if (file.size / 1024 / 1024 > maxSizeMB) {
              setFieldError(
                "addCategoryImage",
                `File is too large. Maximum allowed size is ${maxSizeMB} MB.`
              );
              setCategoryImage(null);
              e.target.value = "";
              return;
            }

            //  valid file
            setCategoryImage(file);
            setFieldError("addCategoryImage", "");
            setFieldError("addCategoryApi", "");
            setApiError("");

            // OPTIONAL: preview if you have a preview state
            // setPreview(URL.createObjectURL(file));
          }}
        />


        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5 }}>
          <Button
            variant="contained"
            onClick={handlePickFile}
            disabled={!!isReadOnly || loading}
            sx={{
              textTransform: "none",
              height: 40,
              minWidth: 150,
              borderRadius: "8px",
              backgroundColor: "#e6cf87",   // same family as Save
              color: "#1f2937",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#dcc46d",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "#f3f4f6",
                color: "#9ca3af",
              },
            }}
          >
            Upload image
          </Button>


          <Typography sx={{ fontSize: 12, color: "#6a7282" }}>
            {categoryImage ? categoryImage.name : "No file selected"}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: 12,
            color: errors?.addCategoryImage ? "#d32f2f" : "transparent",
            mb: 1,
          }}
        >
          {errors?.addCategoryImage || " "}
        </Typography>

        {/* 4) Category Description */}
        <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.75 }}>
          Category Description
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder="Enter category description (optional)"
          value={categoryDescription}
          onChange={(e) => {
            if (isReadOnly || loading) return;
            setCategoryDescription(e.target.value);
            setFieldError("addCategoryApi", "");
            setApiError("");
          }}
          disabled={!!isReadOnly || loading}
          size="small"
          sx={{ mb: 2 }}
        />

        {/* API Error */}
        {(apiError || errors?.addCategoryApi) && (
          <div className="error-message">
            {apiError || errors.addCategoryApi}
          </div>
        )}

        <div className="modal-actions buttons-wrapper">
          <button
            type="button"
            onClick={handleClose}
            className="cancel-button"
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="button"
            className="primary submit-button"
            onClick={handleSave}
            disabled={!!isReadOnly || loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCategoryModal;
